import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { ChartVisualizationData, DetailedChart } from '~/components/charts/types'
import {
  useGetChartVisualizationDataLazyQuery,
  useGetDetailedChartLazyQuery,
  useGetTablesSimpleQuery,
} from '../../common/generated/apollo-operations'
import { SimpleTable } from '../tables/types'

interface ChartDetailsContextValue {
  chartId: string
  chart: DetailedChart | null
  chartVisualization: ChartVisualizationData | null
  // Tables filtered by full view access
  tables: SimpleTable[]
  loading: boolean
  hasError: boolean
  errorMessage: string | null
  tablesLoading: boolean
  isStandaloneChart: boolean
  // Only defined if the chart is being viewed in a dashboard
  columnSpan?: number
  showLegend?: boolean
}

export function ChartDetailsProvider({
  children,
  chartId,
  columnSpan,
}: {
  children: React.ReactNode
  chartId?: string
  columnSpan?: number
}) {
  const { data: tablesData, loading: tablesLoading } = useGetTablesSimpleQuery({
    variables: { input: { fullViewAccess: true } },
  })
  const runOnce = useRef(true)
  const [fetchChart, { data: chartData, loading: chartLoading }] = useGetDetailedChartLazyQuery({
    fetchPolicy: 'cache-and-network',
  })

  const [fetchChartVisualization, { data: visData, loading, error }] =
    useGetChartVisualizationDataLazyQuery({
      fetchPolicy: 'cache-and-network',
    })

  const chart = useMemo(() => {
    return chartData?.chart || null
  }, [chartData?.chart])

  const chartVisualization = useMemo(() => {
    return chartData?.chart && chartData.chart.id === chartId
      ? visData?.chartVisualizationData || null
      : null
  }, [visData?.chartVisualizationData, chartData?.chart, chartId])

  const tables = useMemo(() => {
    return tablesData?.tables || []
  }, [tablesData?.tables])

  const hasError = useMemo(() => {
    return error !== undefined
  }, [error])

  const errorMessage = useMemo(() => {
    return error?.message || null
  }, [error])

  useEffect(() => {
    if (chartId) {
      fetchChart({
        variables: {
          chartId,
        },
      })
    }
  }, [chartId, fetchChart])

  useEffect(() => {
    if (
      chartId &&
      chartData?.chart &&
      chartId === chartData.chart.id &&
      !chartLoading &&
      runOnce.current
    ) {
      fetchChartVisualization({
        variables: {
          input: { chartId },
        },
      })
      runOnce.current = false
    }
  }, [chartId, chartData?.chart, fetchChartVisualization, chartLoading])

  return (
    <ChartDetailsContext.Provider
      value={{
        chartId: chartId || '',
        chart,
        chartVisualization,
        tables,
        loading,
        hasError,
        errorMessage,
        tablesLoading,
        isStandaloneChart: columnSpan === undefined,
        columnSpan,
        showLegend: true,
      }}
    >
      {children}
    </ChartDetailsContext.Provider>
  )
}

export const useChartDetails = () => {
  const context = useContext(ChartDetailsContext)

  if (!context) {
    throw new Error('useChartDetails must be used within a ChartDetailsProvider')
  }

  return context
}

const ChartDetailsContext = createContext<ChartDetailsContextValue | null>(null)
